/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-10 16:41:32                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:15:28                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Validator
  * ContactInfoForm: contact info form component
*/

/* Imports */
import { t } from "i18next";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useState, useEffect } from "react";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
/***/

/* Validator */
export const ContactInfoFormValidator = {
  email: (e: unknown) => validator.isString(e) || (Array.isArray(e) && e.length > 0),
  emails: (e: unknown) => Array.isArray(e) && e.length > 0,
  phone: (e: unknown) => validator.isString(e), // todo check phone format
  fax: (e: unknown) => true, // optional field
  notes: (e: unknown) => true // optional field
};

const v = ContactInfoFormValidator; // shorthand
/***/

/**
* ContactInfoForm: contact info form component
* allow create and edit contact info
* @param data - data to edit, null to creation
* @param onChange - on value change event
* @param onSubmit - on submit event
* @param submitted - form submitted state
* @param hideNotes - drop the general note field, for forms that render it in their own notes section
*/
export default function ContactInfoForm({
  data,
  onChange,
  onSubmit,
  submitted,
  hideNotes = false
}: {
  data?: EasyTech.ContactInfo,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  hideNotes?: boolean
}) {
  const [emailValues, setEmailValues] = useState<{label: string, value: string}[]>([]);

  useEffect(() => {
    if (data?.emails && Array.isArray(data.emails)) {
      setEmailValues(data.emails.map(email => ({ label: email, value: email })));
    } else if (data?.email) {
      setEmailValues([{ label: data.email, value: data.email }]);
      if (!data?.emails) {
        onChange("emails", [data.email]);
      }
    } else if (data?.mail) {
      setEmailValues([{ label: data.mail, value: data.mail }]);
      if (!data?.emails) {
        onChange("emails", [data.mail]);
      }
    } else {
      setEmailValues([]);
    }
  }, [data?.emails, data?.email, data?.mail]);

  const handleEmailChange = (newValue: any) => {
    const emails = newValue ? newValue.map((opt: any) => opt.value) : [];
    
    // Mettre à jour la liste des emails
    onChange("emails", emails);
    
    // Mettre à jour l'email principal pour la compatibilité
    if (emails.length > 0) {
      onChange("email", emails[0]);
      onChange("mail", emails[0]);
    } else {
      onChange("email", "");
      onChange("mail", "");
    }
  };

  const loadOptions = (inputValue: string): Promise<{label: string, value: string}[]> => {
    return Promise.resolve([]);
  };

  return (
    <>
      <div className="field">
        <label>{t("common:email")} *</label>
        <AsyncCreatableSelect
          isMulti
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          value={emailValues}
          onChange={handleEmailChange}
          placeholder={t("common:email")}
          className={`w-full ${(submitted && !validator.isFieldValid(data, "emails", v)) && "error"}`}
          classNamePrefix="react-select"
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "#41A1FF",
            },
          })}
          formatCreateLabel={(inputValue) => `${t("common:add")} "${inputValue}"`}
          noOptionsMessage={() => null}
        />
      </div>

      <div className="field">
        <label>{t("common:phone")} *</label>
        <input  type="text"
                name="phone"
                className={`form-field ${(submitted && !validator.isFieldValid(data, "phone", v)) && "error"}`}
                placeholder="ex: 0X XX XX XX XX"
                value={data?.phone}
                onChange={(e) => onChange(e.target.name, e.target.value)}/>
      </div>

      <div className="field">
        <label>{t("common:fax")}</label>
        <input  type="text"
                name="fax"
                className="form-field"
                placeholder="ex: 0X XX XX XX XX"
                value={data?.fax}
                onChange={(e) => onChange(e.target.name, e.target.value)}/>
      </div>

      {/*
        * The general note belongs to the entity, not to its contact info -- both callers intercept
        * "notes" in onChange to route it elsewhere. CustomerForm now shows it in its own Notes
        * group and hides it here; ContactForm still renders it inline.
        */}
      {!hideNotes && (
        <div className="field">
          <label>{t("common:notes")}</label>
          <textarea name="notes"
                    className="form-field"
                    placeholder="ex: Lorem ipsum..."
                    value={data?.notes}
                    onChange={(e) => onChange(e.target.name, e.target.value)}/>
        </div>
      )}
    </>
  );
}
